import React, { useState, useRef, useEffect, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./styles/Navbar.css";
import { useAuth } from "../providers/AuthProvider";
import config from "../configs/config";
import {
  FaCode,
  FaCheckCircle,
  FaTable,
  FaFlag,
  FaBalanceScale,
  FaChartBar,
  FaTachometerAlt,
  FaHome,
  FaPencilRuler,
  FaInfoCircle,
  FaEdit,
} from "react-icons/fa";
import ProjectDetails from "../components/ProjectDetails";
import VariableContext from "../contexts/VariableContext";

function Navbar() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  const [allowedFeatures, setAllowedFeatures] = useState([]);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);

  // --- Prompts modal state ---
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const [prompts, setPrompts] = useState({ prompt1: "", prompt2: "", prompt3: "" });
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsSaveStatus, setPromptsSaveStatus] = useState({ type: "", message: "" });
  const [promptsViewMode, setPromptsViewMode] = useState(false);

  const {
    setOriginalDataRows,
    selectedProjectDetails,
    setSelectedProjectDetails,
    loading,
    setLoading,
    setBanners,
    setdataGroup,
    dataGroup,
    projectId,
    setProjectId,
    setFrequencyData,
    setWeightingStats,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    setVariables,
    setSelectedClient,
    setSelectedProject,
    selectedProject,
    selectedClient,
    setSdataGroup,
    powner,
    setPowner,
    setSurveyFileName,
    setSurveyName,
  } = useContext(VariableContext);

  const dropdownRef = useRef(null);
  const hideLinksOnPaths = ["/home", "/login"];

  const navItems = [
    { path: "/surveybuilder", label: "Scripting", feature: "scripting", icon: <FaCode /> },
    { path: "/datacheck", label: "Data Validate", feature: "datacheck", icon: <FaCheckCircle /> },
    { path: "/datatables", label: "Data Tables", feature: "datatables", icon: <FaTable /> },
    { path: "/banner", label: "Banner", feature: "banner", icon: <FaFlag /> },
    { path: "/weighting", label: "Weighting", feature: "weighting", icon: <FaBalanceScale /> },
    { path: "/dataanalysis", label: "Data Analysis", feature: "dataanalysis", icon: <FaChartBar /> },
    { path: "/dashboardbuilder", label: "Dashboard", feature: "dashboard", icon: <FaTachometerAlt /> },
    { path: "/home", label: "Home", feature: "home", icon: <FaHome /> },
    { path: "/qnrdesign", label: "Qnr Design", feature: "qnrdesign", icon: <FaPencilRuler />, badge: "(In Development)" },
    { path: "/crosstabmodel", label: "Cross Tab", feature: "crosstab", icon: <FaTable /> },
    { path: "/data", label: "TEST Data", feature: "crosstab", icon: <FaTable /> },
  ];

  const getInitial = (email) => (email ? email.charAt(0).toUpperCase() : "U");
  const getFullNameFromEmail = (email) => {
    if (!email) return "";
    const namePart = email.split("@")[0];
    const nameParts = namePart.split(/[._]/);
    return nameParts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
  };

  const userEmail = user?.user?.email || user?.email || "";
  const userName = getFullNameFromEmail(userEmail);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization) {
      if (organization.logo_url) setOrgLogo(organization.logo_url);
      if (organization.features && Array.isArray(organization.features) && organization.features.length > 0) {
        setAllowedFeatures(organization.features);
      } else {
        setAllowedFeatures(navItems.map((item) => item.feature));
      }
    } else {
      setAllowedFeatures(navItems.map((item) => item.feature));
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasFeatureAccess = (feature) => allowedFeatures.includes(feature);
  const closeProjectDetailsModal = () => setShowProjectDetailsModal(false);

  // --- Prompts handlers ---
  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/prompts`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPrompts(data);
      } else {
        console.error("Failed to fetch prompts");
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
    } finally {
      setPromptsLoading(false);
    }
  };

  const savePrompts = async () => {
    setPromptsSaveStatus({ type: "info", message: "Saving..." });
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/prompts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(prompts),
      });
      if (response.ok) {
        setPromptsSaveStatus({ type: "success", message: "Saved successfully!" });
        setTimeout(() => setPromptsSaveStatus({ type: "", message: "" }), 3000);
      } else {
        setPromptsSaveStatus({ type: "error", message: "Save failed" });
      }
    } catch (error) {
      setPromptsSaveStatus({ type: "error", message: "Network error" });
    }
  };

  const openPromptsModal = () => {
    setShowPromptsModal(true);
    setPromptsViewMode(false);
    fetchPrompts();
    setShowDropdown(false);
  };

  const closePromptsModal = () => {
    setShowPromptsModal(false);
    setPromptsSaveStatus({ type: "", message: "" });
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <img
            src={orgLogo || "https://excavateresearch.com/static/media/logo-small.89d00b404bbe8926034f.png"}
            alt="Organization Logo"
            className="logo"
          />
          <div className="nav-links">
            {!hideLinksOnPaths.includes(location.pathname) && (
              <>
                {navItems.map(
                  (item) =>
                    hasFeatureAccess(item.feature) && (
                      <NavLink key={item.path} to={item.path} className="nav-item">
                        <div className="nav-icon">{item.icon}</div>
                        <span className="nav-label">{item.label}</span>
                        {item.badge && <small className="beta-tag">{item.badge}</small>}
                      </NavLink>
                    )
                )}
              </>
            )}
          </div>
          <div className="profile-section" ref={dropdownRef}>
            <button
              className="project-details-btn"
              onClick={() => setShowProjectDetailsModal(true)}
              title="View Project Details"
            >
              <FaInfoCircle />
            </button>

            <div className="profile-avatar" onClick={() => setShowDropdown(!showDropdown)}>
              {getInitial(userEmail)}
            </div>

            {showDropdown && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">{getInitial(userEmail)}</div>
                  <div className="dropdown-info">
                    <div className="dropdown-name">{userName}</div>
                    <div className="dropdown-email">{userEmail}</div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>

                <button className="dropdown-edit-prompts" onClick={openPromptsModal}>
                  <FaEdit style={{ marginRight: "8px" }} /> Edit System Prompts
                </button>

                <div className="dropdown-divider"></div>
                <button className="dropdown-logout" onClick={logout}>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Project Details Modal */}
      {showProjectDetailsModal && (
        <div className="modal-overlay" onClick={closeProjectDetailsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Project Details</h2>
              <button className="modal-close-btn" onClick={closeProjectDetailsModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <ProjectDetails project={selectedProjectDetails} projectid={projectId} projectOwner={powner} />
            </div>
            <div className="modal-footer">
              <button className="modal-close-button" onClick={closeProjectDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompts Modal */}
      {showPromptsModal && (
        <div className="modal-overlay" onClick={closePromptsModal}>
          <div className="modal-content prompts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{promptsViewMode ? "View System Prompts" : "Edit System Prompts"}</h2>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button
                  className="modal-toggle-mode-btn"
                  onClick={() => setPromptsViewMode(!promptsViewMode)}
                  title={promptsViewMode ? "Switch to Edit mode" : "Switch to View mode"}
                >
                  {promptsViewMode ? (
                    <>
                      <FaEdit style={{ marginRight: "6px" }} /> Edit
                    </>
                  ) : (
                    <>
                      <FaInfoCircle style={{ marginRight: "6px" }} /> View
                    </>
                  )}
                </button>
                <button className="modal-close-btn" onClick={closePromptsModal}>
                  &times;
                </button>
              </div>
            </div>

            <div className="modal-body">
              {promptsLoading ? (
                <div className="loading-spinner">Loading prompts...</div>
              ) : (
                <>
                  <div className="prompt-field">
                    <label>Data Tables Prompt (datatablesprompt.txt)</label>
                    {promptsViewMode ? (
                      <pre className="prompt-view-box">{prompts.prompt1 || "(empty)"}</pre>
                    ) : (
                      <textarea
                        rows="6"
                        value={prompts.prompt1}
                        onChange={(e) => setPrompts({ ...prompts, prompt1: e.target.value })}
                        placeholder="Enter prompt for Data Tables..."
                      />
                    )}
                  </div>

                  <div className="prompt-field">
                    <label>Data Validation Prompt (datavalidationprompt.txt)</label>
                    {promptsViewMode ? (
                      <pre className="prompt-view-box">{prompts.prompt2 || "(empty)"}</pre>
                    ) : (
                      <textarea
                        rows="6"
                        value={prompts.prompt2}
                        onChange={(e) => setPrompts({ ...prompts, prompt2: e.target.value })}
                        placeholder="Enter prompt for Data Validation..."
                      />
                    )}
                  </div>

                  <div className="prompt-field">
                    <label>Survey Scripting Prompt (surveyscriptingprompt.txt)</label>
                    {promptsViewMode ? (
                      <pre className="prompt-view-box">{prompts.prompt3 || "(empty)"}</pre>
                    ) : (
                      <textarea
                        rows="6"
                        value={prompts.prompt3}
                        onChange={(e) => setPrompts({ ...prompts, prompt3: e.target.value })}
                        placeholder="Enter prompt for Survey Scripting..."
                      />
                    )}
                  </div>

                  {!promptsViewMode && promptsSaveStatus.message && (
                    <div className={`save-status ${promptsSaveStatus.type}`}>
                      {promptsSaveStatus.message}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              {!promptsViewMode && (
                <button
                  className="modal-save-button"
                  onClick={savePrompts}
                  disabled={promptsLoading}
                >
                  Save Changes
                </button>
              )}
              <button className="modal-close-button" onClick={closePromptsModal}>
                {promptsViewMode ? "Close" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;